import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
  Component,
  ChangeDetectionStrategy,
  Input,
  Output,
  EventEmitter,
  ContentChildren,
  AfterContentInit,
  AfterViewInit,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ElementRef,
  QueryList,
  HostListener,
  HostBinding,
} from '@angular/core';

/**
 * External Libraries
 */
import { Modal } from 'bootstrap';

/**
 * Enums
 */
import { IModalFullScreenSize, IModalSize } from './enums/modal';

/**
 * Interfaces
 */
import { ITemplateRef } from '@sharedDirectives/templates/interfaces/template';

/**
 * Directives
 */
import { ButtonDirective } from '@sharedDirectives/button/button.directive';
import { TemplateDirective } from '@sharedDirectives/templates/template.directive';


/**
 * @component
 * @description Highly flexible Bootstrap modal for Angular. Supports content projection and advanced template injection for header, body, and footer.
 */
@Component({
  selector: 'app-modal',
  templateUrl: './modal.component.html',
  styleUrl: './modal.component.scss',
  exportAs: 'appModalApi',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[id]': 'id',
    '[tabindex]': '-1',
    '[attr.role]': 'role',
    '[class.fade]': 'fade',
    '[class.modal]': 'true',
    '[class.modal-top]': 'slideFromTop',
    '[attr.data-bs-backdrop]': 'backdrop',
    '[class.modal-transparent]': 'transparentBg',
    '[class.modal-onboarding]': '!!OnboardingTemplate',
  },
  imports: [
    NgClass,
    NgTemplateOutlet,
    ButtonDirective
]
})
export class ModalComponent implements AfterContentInit, AfterViewInit, OnChanges, OnDestroy {

  //--------------------------------------------------------------------------
  // Event Handling (Host Listeners)
  //--------------------------------------------------------------------------

  /**
   * @event
   * Handles when modal is about to be shown via Bootstrap event.
   * Emits visibleChange as true.
   * @param $event - Bootstrap show.bs.modal event
   */
  @HostListener('show.bs.modal', ['$event'])
  public showModal($event: Event) {
    this.visible = true;
    this.visibleChange.emit(true);
  }

  /**
   * @event
   * Handles when modal is completely hidden via Bootstrap event.
   * Emits visibleChange as false.
   * @param $event - Bootstrap hidden.bs.modal event
   */
  @HostListener('hidden.bs.modal', ['$event'])
  public hiddenModal($event: Event) {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  //--------------------------------------------------------------------------
  // Content Projection and Template Injection
  //--------------------------------------------------------------------------

  /**
   * @description Query for all custom templates projected via TemplateDirective.
   */
  @ContentChildren(TemplateDirective)
  public TemplatesDirectiveRef!: QueryList<TemplateDirective>;

  //--------------------------------------------------------------------------
  // Inputs (Configuration)
  //--------------------------------------------------------------------------

  /**
   * @input Unique ID for the modal element.
   */
  @Input({ alias: 'identifier' })
  public id!: string;

  /**
   * @input Show/hide top right corner close button.
   */
  @Input({ alias: 'cornerButton' })
  public cornerButton: boolean = true;

  /**
   * @input Modal body as plain text (used as fallback).
   */
  @Input({ alias: 'bodyText' })
  public bodyText!: string;

  /**
   * @input Modal header title.
   */
  @Input({ alias: 'title' })
  public title!: string;

  /**
   * @input Label for the close/cancel button.
   */
  @Input({ alias: 'closeLabel' })
  public closeLabel: string = 'Close';

  /**
   * @input Label for the accept/confirm button.
   */
  @Input({ alias: 'acceptLabel' })
  public acceptLabel: string = 'Save changes';

  /**
   * @input ARIA role for the modal dialog (default: dialog).
   */
  @Input({ alias: 'role' })
  public role: string = 'dialog';

  /**
   * @input Controls visibility of the modal.
   */
  @Input({ alias: 'visible', required: true })
  public visible: boolean = false;

  /**
   * @input Enable fade animation.
   */
  @Input({ alias: 'fade' })
  public fade: boolean = true;

  /**
   * @input Custom modal animation CSS class.
   */
  @HostBinding('class')
  @Input({
    alias: 'animation',
    transform: (value: string) => `animate__animated ${value}`
  })
  public animation!: string;

  /**
   * @input Additional custom CSS classes for the modal.
   */
  @Input({ alias: 'classStyle' })
  public classStyle!: string;

  /**
   * @input Controls modal backdrop behavior ("static", true, or false).
   */
  @Input({ alias: 'backdrop' })
  public backdrop: "static" | boolean = true;

  /**
   * @input Allow closing modal with escape key.
   */
  @Input({ alias: 'escapeKey' })
  public escapeKey: boolean = true;

  /**
   * @input Focus modal when opened.
   */
  @Input({ alias: 'focusOnModal' })
  public focus: boolean = true;

  /**
   * @input Center modal vertically in viewport.
   */
  @Input({ alias: 'centered' })
  public dialogCentered: boolean = false;

  /**
   * @input Enable scrollable modal dialog.
   */
  @Input({ alias: 'scrollable' })
  public dialogScrollable: boolean = false;

  /**
   * @input Enable slide-in from top animation.
   */
  @Input({ alias: 'slideTop' })
  public slideFromTop: boolean = false;

  /**
   * @input Enable transparent background.
   */
  @Input({ alias: 'transparent' })
  public transparentBg: boolean = false;

  /**
   * @input Fullscreen modal configuration.
   * Can be boolean or enum value for specific breakpoints.
   */
  @Input({ alias: 'fullscreen' })
  public dialogFullscreen: boolean | IModalFullScreenSize = false;

  /**
   * @input Modal dialog size (sm, lg, xl, etc).
   */
  @Input({ alias: 'size' })
  public dialogSize!: IModalSize;

  //--------------------------------------------------------------------------
  // Outputs (Events)
  //--------------------------------------------------------------------------

  /**
   * @output Emits when modal visibility changes (two-way binding).
   */
  @Output()
  public visibleChange = new EventEmitter<boolean>();

  /**
   * @output Emits when modal is closed by any means.
   */
  @Output()
  public closedAction = new EventEmitter<void>();

  /**
   * @output Emits when modal accept/confirm action is triggered.
   */
  @Output()
  public acceptAction = new EventEmitter<void>();

  //--------------------------------------------------------------------------
  // Internal Fields
  //--------------------------------------------------------------------------

  /**
   * @description Holds the resolved template reference for header section.
   */
  public HeaderTemplate!: ITemplateRef<any> | null;

  /**
   * @description Holds the resolved template reference for body section.
   */
  public BodyTemplate!: ITemplateRef<any> | null;

  /**
   * @description Holds the resolved template reference for footer section.
   */
  public FooterTemplate!: ITemplateRef<any> | null;

  /**
   * @description Holds the resolved template reference for onboarding (if used).
   */
  public OnboardingTemplate!: ITemplateRef<any> | null;

  /**
   * @description Bootstrap modal instance for programmatic control.
   */
  public _modalInstance?: Modal;

  /**
   * @description Used to prevent triggering show/hide logic on first render.
   */
  private _isInitialRender = true;

  /**
   * 
   */
  public get dialogFullscreenClass(): string {
    return this.dialogFullscreen ? `modal-fullscreen-${this.dialogFullscreen}-down` : '';
  }

  /**
   * 
   */
  public get dialogSizeClass(): string {
    return this.dialogSize ? `modal-${this.dialogSize}` : '';
  }

  /**
   * 
   */
  public get dialogCenteredClass(): string {
    return this.dialogCentered ? 'modal-dialog-centered' : '';
  }

  /**
   * @description
   */
  public get dialogScrollableClass(): string {
    return this.dialogScrollable ? 'modal-dialog-scrollable' : '';
  }

  //--------------------------------------------------------------------------
  // Constructor
  //--------------------------------------------------------------------------

  /**
   * @constructor
   * Ensures modal receives a unique ID if not provided.
   * @param _elRef - Reference to modal host element.
   */
  constructor(private _elRef: ElementRef<HTMLElement>) {
    if (!this.id) {
      this.id = `modalComponent_id_${Math.random().toString(36).slice(2, 10)}`;
    }
  }

  //--------------------------------------------------------------------------
  // Lifecycle Hooks
  //--------------------------------------------------------------------------

  /**
   * @lifecycle After content projection is initialized.
   * Resolves template references for each section.
   */
  ngAfterContentInit(): void {
    if (this.TemplatesDirectiveRef && this.TemplatesDirectiveRef.length > 0) {
      this.HeaderTemplate = TemplateDirective.findDirective<any>(this.TemplatesDirectiveRef, 'headerTemplate');
      this.BodyTemplate   = TemplateDirective.findDirective<any>(this.TemplatesDirectiveRef, 'bodyTemplate');
      this.FooterTemplate = TemplateDirective.findDirective<any>(this.TemplatesDirectiveRef, 'footerTemplate');
      this.OnboardingTemplate = TemplateDirective.findDirective<any>(this.TemplatesDirectiveRef, 'onboardingTemplate');
    }
  }

  /**
   * @lifecycle After view initialization.
   * Creates the Bootstrap modal instance and auto-shows if visible.
   */
  ngAfterViewInit(): void {
    this._modalInstance = new Modal(this._elRef.nativeElement, {
      focus: this.focus,
      backdrop: this.backdrop,
      keyboard: this.escapeKey,
    });
    if (this.visible) {
      this._modalInstance.show();
    }
    this._isInitialRender = false;
  }

  /**
   * @lifecycle Responds to changes in input properties.
   * Handles showing/hiding modal on visible changes.
   * @param changes - SimpleChanges map
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (!this._modalInstance) return;
    if ('visible' in changes && !this._isInitialRender) {
      if (this.visible) {
        this.show();
      } else {
        this.hide();
      }
    }
  }

  /**
   * @lifecycle Cleans up modal instance on destroy.
   */
  ngOnDestroy(): void {
    this._modalInstance?.dispose();
  }

  //--------------------------------------------------------------------------
  // Public Methods
  //--------------------------------------------------------------------------

  /**
   * @method Open the modal programmatically.
   */
  public show(): void {
    this._modalInstance?.show();
  }

  /**
   * @method Close the modal programmatically and blur any focused descendant.
   */
  public hide(): void {
    this.blurActiveElementIfInsideModal();
    this._modalInstance?.hide();
  }

  /**
   * @method Trigger accept/confirm action (closes modal and emits accept event).
   */
  public accept(): void {
    this.hide();
    this.acceptAction.emit();
  }

  //--------------------------------------------------------------------------
  // Private/Utility Methods
  //--------------------------------------------------------------------------

  /**
   * @method Removes focus from any active element inside the modal.
   * Prevents a11y warning when hiding modal with focused content.
   */
  private blurActiveElementIfInsideModal(): void {
    const activeEl = document.activeElement as HTMLElement;
    if (this._elRef.nativeElement.contains(activeEl)) {
      activeEl.blur();
    }
  }

}
